import React, { useEffect, useState } from 'react';
import { MessageList } from './MessageList';
import { MessageForm } from './MessageForm';
import TeamBlogHeader from './TeamBlogHeader';
import { useTeamMessages } from './useTeamMessages';
import { useMessageHandlers } from './useMessageHandlers';
import { useParams, Navigate } from "react-router-dom";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useNotifications } from "@/hooks/notifications";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { BellRing } from "lucide-react";
import AppLayout from "@/components/layout/AppLayout";
import { useIsMobile } from "@/hooks/use-mobile";

const TeamBlog = () => {
  const { teamId } = useParams();
  const { messages, isAdmin, isLoading, setIsLoading, removeMessage, clearUnreadCount } = useTeamMessages(teamId);
  const { isAdmin: isAdminRole } = useUserRoles();
  const { permissionGranted, initialize, serviceBlocked, serviceWorkerRegistered } = useNotifications();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [teamName, setTeamName] = useState<string>("");

  if (!teamId) {
    console.error("TeamBlog: No teamId provided in URL params");
    return <Navigate to="/teams" replace />;
  }
  
  const {
    newMessage,
    setNewMessage,
    handleSubmit,
    handleDeleteMessage,
    handleImageUpload,
    handleEditMessage,
  } = useMessageHandlers(teamId, setIsLoading);

  useEffect(() => {
    const fetchTeamName = async () => {
      if (!teamId) return;
      
      try {
        const { data, error } = await supabase
          .from('teams')
          .select('name')
          .eq('id', teamId)
          .single();
          
        if (error) {
          console.error("Error fetching team name:", error);
          setTeamName(`Team ${teamId}`);
          return;
        }
        
        if (data) {
          setTeamName(data.name);
        } else {
          setTeamName(`Team ${teamId}`);
        }
      } catch (err) {
        console.error("Error in fetchTeamName:", err);
        setTeamName(`Team ${teamId}`);
      }
    };
    
    fetchTeamName();
  }, [teamId]);

  const onDeleteMessage = async (messageId: string) => {
    const success = await handleDeleteMessage(messageId);
    if (success) {
      removeMessage(messageId);
    }
  };

  useEffect(() => {
    const initNotifications = async () => {
      try {
        await initialize(false);
      } catch (error) {
        console.error("Error initializing notifications:", error);
      }
    };

    initNotifications();
    
    if (teamId) {
      clearUnreadCount();
    }
  }, [teamId, clearUnreadCount, initialize]);

  const requestBackgroundNotifications = async () => {
    try {
      const success = await initialize(true);
      
      if (success) {
        toast({
          title: 'Notification Permission Granted',
          description: 'You will receive notifications for new messages.',
          duration: 5000,
        });
      } else if (serviceBlocked) {
        toast({
          title: 'Notification Services Blocked',
          description: 'Google services appear to be blocked on your device. Please check your device settings.',
          duration: 8000,
        });
      }
    } catch (error) {
      console.error("Error requesting notifications:", error);
      toast({
        title: 'Error',
        description: 'There was a problem setting up notifications.',
        duration: 5000,
      });
    }
  };

  const sendNotification = async (messageId: string, content: string, authorName: string) => {
    if (!teamId) return;
    
    try {
      toast({
        title: `New message from ${authorName}`,
        description: content || 'Image message',
        duration: 5000,
      });
      
      try {
        const response = await supabase.functions.invoke('send-notification', {
          body: {
            teamId,
            messageId,
            title: `New message from ${authorName}`,
            body: content || 'Image message',
            isMobile: true,
            mobileOS: /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'ios' : 'android'
          }
        });
        
        console.log('Notification function response:', response);
      } catch (error) {
        console.log('Expected notification function error in sandbox:', error);
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  useEffect(() => {
    if (!messages.length || !teamId) return;
    
    const lastMessage = messages[messages.length - 1];
    
    const getUserPromise = async () => {
      const { data } = await supabase.auth.getUser();
      return data.user;
    };
    
    getUserPromise().then(user => {
      if (user && lastMessage && lastMessage.author.id !== user.id) {
        sendNotification(
          lastMessage.id, 
          lastMessage.content, 
          lastMessage.author.full_name || 'Team member'
        );
      }
    });
  }, [messages, teamId, toast]);

  return (
    <AppLayout>
      <div className="h-[calc(100vh-4rem)] overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        {/* Header */}
        <div className="bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex justify-between items-center">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                    <span className="text-white font-semibold text-sm">
                      {teamName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-foreground">
                      {teamName}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Team Chat • {messages.length} message{messages.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
              
              {isMobile && (
                <Button 
                  onClick={requestBackgroundNotifications}
                  variant="outline"
                  className="bg-background/50 hover:bg-background/80 border-border/50 hover:border-primary/30 flex items-center gap-2 transition-all duration-200 hover:shadow-md"
                  size="sm"
                >
                  <BellRing size={16} className="text-primary" />
                  <span className="hidden sm:inline">Enable Notifications</span>
                  <span className="sm:hidden">Notify</span>
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Chat Container */}
        <div className="h-[calc(100%-6rem)] max-w-4xl mx-auto flex flex-col p-4">
          <div className="flex-1 bg-background/30 backdrop-blur-sm rounded-3xl shadow-2xl border border-border/50 overflow-hidden flex flex-col mb-4">
            <div className="flex-1 overflow-hidden min-h-0">
              <MessageList 
                messages={messages} 
                isAdmin={isAdmin || isAdminRole} 
                onDelete={onDeleteMessage}
                onEdit={handleEditMessage}
              />
            </div>
            
            {/* Message Form */}
            <div className="bg-background/80 backdrop-blur-xl border-t border-border/50 p-4 rounded-b-3xl">
              <MessageForm 
                newMessage={newMessage}
                isLoading={isLoading}
                onMessageChange={setNewMessage}
                onSubmit={handleSubmit}
                onImageUpload={handleImageUpload}
              />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default TeamBlog;
